import {
  addDoc,
  arrayRemove,
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type DocumentData,
} from 'firebase/firestore';
import {
  EXERCISES,
  calibrationSchema,
  programEnrollmentSchema,
  sessionFeedbackSchema,
  sessionSummarySchema,
  userConsentSchema,
  userProfileSchema,
  userSettingsSchema,
} from '@erectus/shared';
import type {
  BreakLogEntry,
  Calibration,
  Exercise,
  ProgramEnrollment,
  SnackLogEntry,
  PostureCheck,
  Recommendation,
  SessionFeedback,
  SessionSummary,
  UserConsent,
  UserDoc,
  UserProfile,
  UserSettings,
} from '@erectus/shared';
import { httpsCallable } from 'firebase/functions';
import { firebase } from './firebase.js';
import { markStale } from './warmCache.js';
import { deleteLocalSession } from '@/session/db.js';
import { getDocsServerFirst } from './serverFirst.js';

/**
 * Firestore access layer. Every write is validated with the shared zod schema
 * *before* it leaves the client, so a bug here fails loudly in development
 * instead of quietly corrupting a session document.
 */

export function userRef(uid: string) {
  return doc(firebase().db, 'users', uid);
}

export async function getUser(uid: string): Promise<UserDoc | null> {
  const snapshot = await getDoc(userRef(uid));
  return snapshot.exists() ? (snapshot.data() as UserDoc) : null;
}

export async function saveProfile(uid: string, profile: UserProfile): Promise<void> {
  await setDoc(userRef(uid), { profile: userProfileSchema.parse(profile) }, { merge: true });
}

export async function saveSettings(uid: string, settings: UserSettings): Promise<void> {
  await setDoc(userRef(uid), { settings: userSettingsSchema.parse(settings) }, { merge: true });
}

export async function saveConsent(uid: string, consent: UserConsent): Promise<void> {
  await setDoc(userRef(uid), { consent: userConsentSchema.parse(consent) }, { merge: true });
}

export async function saveCalibration(uid: string, calibration: Calibration): Promise<void> {
  await setDoc(
    userRef(uid),
    { calibration: calibrationSchema.parse(calibration) },
    { merge: true },
  );
}

/** A fresh id for a posture check, so its keypoint file can be named before the doc is written. */
export function newPostureCheckId(uid: string): string {
  return doc(collection(userRef(uid), 'checks')).id;
}

/** Saves a posture check (create-only by rule). */
export async function savePostureCheck(uid: string, id: string, check: PostureCheck): Promise<void> {
  await setDoc(doc(collection(userRef(uid), 'checks'), id), check);
  markStale(`checks:${uid}`);
}

/** Recent posture checks, newest first; older checks without `kind` read as standing. */
export async function recentPostureChecks(uid: string): Promise<Array<PostureCheck & { id: string }>> {
  const { snapshot } = await getDocsServerFirst(
    query(collection(userRef(uid), 'checks'), orderBy('at', 'desc'), limit(20)),
  );
  return snapshot.docs.map((d) => {
    const data = d.data() as PostureCheck;
    return { ...data, kind: data.kind ?? (data.standing ? 'standing' : 'seated'), id: d.id };
  });
}

/** Optional height for centimetre readouts; null clears it. */
export async function saveHeight(uid: string, heightCm: number | null): Promise<void> {
  if (heightCm !== null && (heightCm < 100 || heightCm > 250)) throw new Error('Height out of range');
  await updateDoc(userRef(uid), { 'profile.heightCm': heightCm === null ? deleteField() : Math.round(heightCm) });
}

export async function markOnboardingComplete(uid: string): Promise<void> {
  await setDoc(userRef(uid), { onboardingCompletedAt: Date.now() }, { merge: true });
}

export async function markTourComplete(uid: string): Promise<void> {
  await setDoc(userRef(uid), { tourCompletedAt: Date.now() }, { merge: true });
}

export async function saveFcmToken(uid: string, token: string): Promise<void> {
  const existing = await getUser(uid);
  const tokens = new Set(existing?.fcmTokens ?? []);
  // Re-adding moves it to the end, so the cap below drops the stalest device.
  tokens.delete(token);
  tokens.add(token);
  await setDoc(userRef(uid), { fcmTokens: [...tokens].slice(-5) }, { merge: true });
}

/** On sign-out: this device must stop receiving the account's reminders. */
export async function removeFcmToken(uid: string, token: string): Promise<void> {
  await updateDoc(userRef(uid), { fcmTokens: arrayRemove(token) });
}

// ── sessions ─────────────────────────────────────────────────────────────────

export function sessionRef(sessionId: string) {
  return doc(firebase().db, 'sessions', sessionId);
}

export async function saveSessionSummary(
  sessionId: string,
  summary: SessionSummary,
): Promise<void> {
  const validated = sessionSummarySchema.parse(summary);
  await setDoc(sessionRef(sessionId), { ...validated, createdAt: serverTimestamp() });
}

export async function getSession(sessionId: string): Promise<SessionSummary | null> {
  const snapshot = await getDoc(sessionRef(sessionId));
  return snapshot.exists() ? (snapshot.data() as SessionSummary) : null;
}

/** Newest first. `provisional` when the server could not be reached (see serverFirst.ts). */
export async function listSessions(
  uid: string,
  max = 30,
): Promise<{ rows: Array<SessionSummary & { id: string }>; provisional: boolean }> {
  const q = query(
    collection(firebase().db, 'sessions'),
    where('uid', '==', uid),
    orderBy('startedAt', 'desc'),
    limit(max),
  );
  const { snapshot, provisional } = await getDocsServerFirst(q);
  return {
    rows: snapshot.docs.map((d) => ({ id: d.id, ...(d.data() as SessionSummary) })),
    provisional,
  };
}

export async function submitFeedback(
  sessionId: string,
  feedback: SessionFeedback,
): Promise<void> {
  const validated = sessionFeedbackSchema.parse(feedback);
  await setDoc(doc(collection(sessionRef(sessionId), 'feedback'), validated.uid), validated);
}

/**
 * Deletes one session everywhere: the server copy with its feedback, coaching,
 * shared keypoints and rollups (`deleteSession` callable), then this device's
 * frames, snapshots and unsynced copy.
 */
export async function deleteSession(sessionId: string): Promise<void> {
  const call = httpsCallable<{ sessionId: string }, { deleted: boolean }>(firebase().functions, 'deleteSession');
  await call({ sessionId });
  await deleteLocalSession(sessionId);
  markStale('sessions:');
}

// ── recommendations & content ────────────────────────────────────────────────

export async function getRecommendationForSession(
  uid: string,
  sessionId: string,
): Promise<(Recommendation & { id: string }) | null> {
  const q = query(
    collection(firebase().db, 'recommendations'),
    where('uid', '==', uid),
    where('sessionId', '==', sessionId),
    limit(1),
  );
  const snapshot = await getDocs(q);
  const first = snapshot.docs[0];
  return first ? { id: first.id, ...(first.data() as Recommendation) } : null;
}

export async function rateRecommendation(recId: string, rating: number): Promise<void> {
  await updateDoc(doc(firebase().db, 'recommendations', recId), {
    userRating: Math.max(1, Math.min(5, Math.round(rating))),
  });
}

/**
 * The exercise library ships in the bundle (@erectus/shared), in the PT
 * list's order (neck, upper back, posture, stretches). It used to be a
 * Firestore read on every visit — a round trip for fixed documents that only
 * change with an app release.
 */
export const EXERCISE_LIBRARY: readonly Exercise[] = [...EXERCISES];

export async function listExercises(): Promise<Exercise[]> {
  return [...EXERCISE_LIBRARY];
}

export interface ExerciseLogEntry {
  exerciseId: string;
  doneAt: number;
}

/** Local calendar day, "2026-10-02" — the day the user means, not UTC's. */
export function localDayKey(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** One entry per exercise per local day; doing it again that day keeps one mark. */
export async function logExerciseDone(uid: string, exerciseId: string): Promise<ExerciseLogEntry> {
  const entry = { exerciseId, doneAt: Date.now() };
  await setDoc(doc(collection(userRef(uid), 'exerciseLog'), `${exerciseId}-${localDayKey(entry.doneAt)}`), entry);
  return entry;
}

/** Exercises done since `sinceMs`, newest first — for the Activity calendar. */
export async function listExerciseLog(uid: string, sinceMs: number): Promise<ExerciseLogEntry[]> {
  const { snapshot } = await getDocsServerFirst(
    query(collection(userRef(uid), 'exerciseLog'), where('doneAt', '>=', sinceMs), orderBy('doneAt', 'desc'), limit(400)),
  ).catch((error: unknown) => {
    // Nothing logged yet and offline is an empty calendar, not an error.
    if ((error as Error).name === 'OfflineEmptyError') return { snapshot: { docs: [] as never[] } };
    throw error;
  });
  return snapshot.docs.map((d) => d.data() as ExerciseLogEntry);
}

/** A rest break taken, from a nudge or on the user's own. */
export async function logBreak(uid: string, source: BreakLogEntry['source']): Promise<BreakLogEntry> {
  const entry: BreakLogEntry = { at: Date.now(), source };
  await addDoc(collection(userRef(uid), 'breakLog'), entry);
  return entry;
}

async function listSince<T>(uid: string, name: string, sinceMs: number): Promise<T[]> {
  const { snapshot } = await getDocsServerFirst(
    query(collection(userRef(uid), name), where('at', '>=', sinceMs), orderBy('at', 'desc'), limit(500)),
  ).catch((error: unknown) => {
    if ((error as Error).name === 'OfflineEmptyError') return { snapshot: { docs: [] as never[] } };
    throw error;
  });
  return snapshot.docs.map((d) => d.data() as T);
}

export function listBreakLog(uid: string, sinceMs: number): Promise<BreakLogEntry[]> {
  return listSince<BreakLogEntry>(uid, 'breakLog', sinceMs);
}

/**
 * An exercise snack finished: one snack entry (so a day counts snacks, not
 * moves) and each move in the exercise log (so the library and the calendar
 * show them as done today).
 */
export async function logSnack(uid: string, entry: Omit<SnackLogEntry, 'at'>): Promise<SnackLogEntry> {
  const saved: SnackLogEntry = { ...entry, at: Date.now() };
  await setDoc(doc(collection(userRef(uid), 'snackLog'), `${entry.snackId}-${localDayKey(saved.at)}`), saved);
  await Promise.all(entry.exerciseIds.map((id) => logExerciseDone(uid, id)));
  return saved;
}

export function listSnackLog(uid: string, sinceMs: number): Promise<SnackLogEntry[]> {
  return listSince<SnackLogEntry>(uid, 'snackLog', sinceMs);
}

/** Start, restart or stop a program. Stopping keeps the record with an end date. */
export async function saveEnrollment(uid: string, enrollment: ProgramEnrollment | null): Promise<void> {
  await setDoc(
    userRef(uid),
    { program: enrollment === null ? null : programEnrollmentSchema.parse(enrollment) },
    { merge: true },
  );
}

/** Optional profile details from onboarding or Settings; null clears one. */
export async function saveBodyDetails(
  uid: string,
  details: { sex?: UserProfile['sex'] | null; weightKg?: number | null },
): Promise<void> {
  const patch: Record<string, unknown> = {};
  if (details.sex !== undefined) patch['profile.sex'] = details.sex === null ? deleteField() : details.sex;
  if (details.weightKg !== undefined) {
    if (details.weightKg !== null && (details.weightKg < 25 || details.weightKg > 300)) throw new Error('Weight out of range');
    patch['profile.weightKg'] = details.weightKg === null ? deleteField() : Math.round(details.weightKg * 10) / 10;
  }
  if (Object.keys(patch).length > 0) await updateDoc(userRef(uid), patch);
}

export async function listWeeklyStats(uid: string): Promise<DocumentData[]> {
  const snapshot = await getDocs(
    query(collection(userRef(uid), 'stats'), orderBy('weekStart', 'desc'), limit(12)),
  );
  return snapshot.docs.map((d) => d.data());
}
